import React, {
  ChangeEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, MoreHoriz } from '@mui/icons-material';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { format } from 'date-fns';
import { trimStart } from 'lodash';
import { useSnackbar } from 'notistack';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

import * as API from '../../services/API';
import APICache from '../../services/APICache';
import { ModelInstance } from '../../types/models';

import useSetState from '../../hooks/useSetState';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';

import APICacheContext from '../shared/APICacheContext';
import PopupDialog from '../shared/PopupDialog';
import StyledMenu from '../shared/StyledMenu';
import OverflowTooltip from '../shared/OverflowTooltip';

import TagsTableContent from './TagsTableContent';
import TagBlockersDialog from './TagBlockersDialog';

const TAG_NAME_LENGTH = 256;
const DESCRIPTION_LENGTH = 4000;

// Shared with ScenarioDetails' Promotion/Subscription combo Loader so a
// force-reload here (see handleAddTag/handleEditTag/handleDeleteTag) hits
// the exact same APICache key and refreshes that combo's options too.
export const SCENARIO_LINK_TAGS_PAGE_SIZE = 100;

export const getScenarioLinkTagsListPath = (clientId?: number): string =>
  `/clients/${clientId}/scenario_link_tags?page=1&pageSize=${SCENARIO_LINK_TAGS_PAGE_SIZE}&sort=name_asc`;

// The list endpoint caps pages at SCENARIO_LINK_TAGS_PAGE_SIZE, so a client with
// more tags would silently lose the rest. Page 1 is loaded through
// `loadFirstPage` on the unchanged path above (so the shared APICache key and
// each caller's existing loading mechanism are preserved); later pages are
// fetched uncached. Stops at the reported total or on an empty page.
export const loadAllScenarioLinkTags = async (
  clientId: number | undefined,
  loadFirstPage: (path: string) => Promise<any> = API.load
): Promise<any[]> => {
  const firstPath = getScenarioLinkTagsListPath(clientId);
  const first = await loadFirstPage(firstPath);
  if (Array.isArray(first)) return first;

  const items: any[] = [...(first?.items ?? [])];
  const total: number = first?.total ?? items.length;

  for (
    let page = 2;
    items.length < total &&
    items.length >= (page - 1) * SCENARIO_LINK_TAGS_PAGE_SIZE;
    page++
  ) {
    const next = await API.load(firstPath.replace('page=1&', `page=${page}&`));
    const nextItems: any[] = next?.items ?? [];
    if (nextItems.length === 0) break;
    items.push(...nextItems);
  }
  return items;
};

const TAG_ERROR_MESSAGES: Record<string, string> = {
  TAG_NAME_CONFLICT:
    'A tag with this name already exists. Please choose a different name.',
  TAG_IN_USE: 'Tag is in use. Remove its assignments before deleting it.',
};

const getTagErrorMessage = (error: any, fallback: string): string => {
  const rawMessage = error?.body?.message;
  return (rawMessage && TAG_ERROR_MESSAGES[rawMessage]) || fallback;
};

interface ScenarioTag {
  id: number;
  name: string;
  description?: string;
  client_id?: number;
  created_at?: string;
  updated_at?: string;
  updated_by?: {
    Display_Name?: string;
    User_Name?: string;
    FirstName?: string;
    LastName?: string;
  };
  updated_by_id?: number;
  created_by?: {
    id?: number;
    FirstName?: string;
    LastName?: string;
  };
  created_by_id?: number;
  deleted_at?: string | null;
}

export type { ScenarioTag };

interface IManageScenarioTagsProps {
  isManageScenarioTagsDialogOpen: boolean;
  handleCloseManageScenarioTagsDialog: () => void;
  modelInstanceId?: ModelInstance['id'];
  clientId?: number;
  onTagsChanged?: () => void;
}

interface IManageScenarioTagsState {
  isAddTagModalOpen: boolean;
  isEditTagModalOpen: boolean;
  showDeleteTagModal: boolean;
  isAiTagsLoading: boolean;
  isPromotionTagsLoading: boolean;
  isModalLoading: boolean;
  aiTags: ScenarioTag[];
  promotionTags: ScenarioTag[];
  selectedTag: ScenarioTag | null;
  tagName: string;
  tagDescription: string;
  onFocusedInput?: string;
  deleteConfirmationName: string;
  activeTabIndex: number;
  promotionTagsPage: number;
  promotionTagsPageSize: number;
  promotionTagsTotal: number;
  tagInUseBlocker: { tagId: number; tagName: string } | null;
}

const initialState: IManageScenarioTagsState = {
  isAddTagModalOpen: false,
  isEditTagModalOpen: false,
  showDeleteTagModal: false,
  isAiTagsLoading: true,
  isPromotionTagsLoading: true,
  isModalLoading: false,
  aiTags: [],
  promotionTags: [],
  selectedTag: null,
  tagName: '',
  tagDescription: '',
  onFocusedInput: undefined,
  deleteConfirmationName: '',
  activeTabIndex: 0,
  promotionTagsPage: 1,
  promotionTagsPageSize: 100,
  promotionTagsTotal: 0,
  tagInUseBlocker: null,
};

const ManageScenarioTags: FunctionComponent<IManageScenarioTagsProps> = (
  props: IManageScenarioTagsProps
) => {
  const [state, setState] = useSetState<IManageScenarioTagsState>(initialState);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { width } = useWindowDimensions();
  const apiCache = useContext(APICacheContext);

  // Guards against a stale, out-of-order response overwriting a newer one.
  const aiTagsRequestId = useRef(0);
  const promotionTagsRequestId = useRef(0);

  // Store the persistent "tag in use" snackbar key so we can close it when the
  // dialog closes, preventing it from lingering on screen.
  const tagInUseSnackbarKeyRef = useRef<string | number | null>(null);

  // Forces ScenarioDetails' Promotion/Subscription combo (which reads the
  // same scenario_link_tags list via the shared APICache) to refetch, so a
  // tag added/edited/deleted here shows up there without a page reload.
  const refreshScenarioLinkTagsCombo = (): void => {
    (apiCache as APICache)?.load(
      [getScenarioLinkTagsListPath(props.clientId)],
      true
    );
  };

  useEffect(() => {
    if (props.isManageScenarioTagsDialogOpen && props.clientId) {
      if (state.activeTabIndex === 0) {
        loadAITags();
      } else {
        loadPromotionTags();
      }
    }
  }, [
    props.isManageScenarioTagsDialogOpen,
    props.clientId,
    state.activeTabIndex,
  ]);

  const loadAITags = async (): Promise<void> => {
    const requestId = ++aiTagsRequestId.current;
    setState({ isAiTagsLoading: true });

    try {
      const response = await API.load(
        `/clients/${props.clientId}/scenario_tags`
      );
      if (requestId !== aiTagsRequestId.current) return;

      // Map API response (PascalCase) to UI format (lowercase)
      const tags = (response || []).map((tag: any) => ({
        id: tag.id,
        name: tag.Name,
        description: tag.Description,
        client_id: tag.ClientID,
        updated_at: tag.updated_at,
        updated_by: tag.updated_by,
        updated_by_id: tag.updated_by_id,
      }));
      setState({
        aiTags: tags,
        isAiTagsLoading: false,
      });
    } catch (error) {
      if (requestId !== aiTagsRequestId.current) return;

      setState({
        aiTags: [],
        isAiTagsLoading: false,
      });
      enqueueSnackbar('Failed to load AI tags', { variant: 'error' });
    }
  };

  const loadPromotionTags = async (page: number = 1): Promise<void> => {
    const requestId = ++promotionTagsRequestId.current;
    setState({ isPromotionTagsLoading: true, promotionTagsPage: page });

    try {
      // Fetch every page so tags beyond the first 100 are not dropped.
      const tagsArray = await loadAllScenarioLinkTags(props.clientId);
      if (requestId !== promotionTagsRequestId.current) return;

      const total = tagsArray.length;
      const pageNumber = page;

      // Map API response to UI format
      const tags = tagsArray.map((tag: any) => ({
        id: tag.id,
        name: tag.Name || tag.name,
        description: tag.Description || tag.description,
        client_id: tag.ClientID || tag.client_id,
        created_at: tag.created_at,
        created_by_id: tag.created_by_id,
        created_by: tag.created_by,
        updated_at: tag.updated_at,
        updated_by: tag.updated_by,
        updated_by_id: tag.updated_by_id,
        deleted_at: tag.deleted_at,
      }));

      setState({
        promotionTags: tags || [],
        promotionTagsTotal: total,
        promotionTagsPage: pageNumber,
        isPromotionTagsLoading: false,
      });
    } catch (error: any) {
      if (requestId !== promotionTagsRequestId.current) return;

      setState({
        promotionTags: [],
        promotionTagsTotal: 0,
        isPromotionTagsLoading: false,
      });

      const errorMessage =
        error?.body?.message ||
        error?.message ||
        `Failed to load submission tags (${error?.status || 'Unknown error'})`;

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const loadTags = async (): Promise<void> => {
    if (state.activeTabIndex === 0) {
      await loadAITags();
    } else {
      await loadPromotionTags();
    }
  };

  const handleAddTagModalOpen = (): void => {
    setState({
      isAddTagModalOpen: true,
      tagName: '',
      tagDescription: '',
    });
  };

  const handleAddTagModalClose = (): void => {
    setState({
      isAddTagModalOpen: false,
      tagName: '',
      tagDescription: '',
    });
  };

  const handleEditTagModalOpen = (tag: ScenarioTag): void => {
    setState({
      isEditTagModalOpen: true,
      selectedTag: tag,
      tagName: tag.name,
      tagDescription: tag.description || '',
    });
  };

  const handleEditTagModalClose = (): void => {
    setState({
      isEditTagModalOpen: false,
      selectedTag: null,
      tagName: '',
      tagDescription: '',
    });
  };

  const handleDeleteTagModalOpen = (tag: ScenarioTag): void => {
    setState({
      showDeleteTagModal: true,
      selectedTag: tag,
    });
  };

  const handleDeleteTagModalClose = (): void => {
    setState({
      showDeleteTagModal: false,
      selectedTag: null,
      deleteConfirmationName: '',
    });
  };

  const handleAddTag = async (): Promise<void> => {
    if (!state.tagName.trim()) {
      enqueueSnackbar('Tag name is required', { variant: 'error' });
      return;
    }
    if (!state.tagDescription.trim()) {
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    setState({ isModalLoading: true });

    try {
      const endpoint =
        state.activeTabIndex === 0
          ? `/clients/${props.clientId}/scenario_tags`
          : `/clients/${props.clientId}/scenario_link_tags`;

      await API.create(endpoint, {
        name: state.tagName.trim(),
        description: state.tagDescription.trim(),
      });

      setState({ isModalLoading: false });
      handleAddTagModalClose();
      enqueueSnackbar('Tag created successfully', { variant: 'success' });

      if (state.activeTabIndex === 0 && props.onTagsChanged) {
        props.onTagsChanged();
      } else if (state.activeTabIndex === 1) {
        refreshScenarioLinkTagsCombo();
      }

      setTimeout(() => {
        loadTags();
      }, 100);
    } catch (error: any) {
      setState({ isModalLoading: false });
      enqueueSnackbar(getTagErrorMessage(error, 'Failed to create tag'), {
        variant: 'error',
      });
    }
  };

  const handleEditTag = async (): Promise<void> => {
    if (!state.tagName.trim() || !state.selectedTag) {
      enqueueSnackbar('Tag name is required', { variant: 'error' });
      return;
    }
    if (!state.tagDescription.trim()) {
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    setState({ isModalLoading: true });

    try {
      const endpoint =
        state.activeTabIndex === 0
          ? `/clients/${props.clientId}/scenario_tags/${state.selectedTag.id}`
          : `/clients/${props.clientId}/scenario_link_tags/${state.selectedTag.id}`;

      const payload = {
        name: state.tagName.trim(),
        description: state.tagDescription.trim(),
      };

      if (state.activeTabIndex === 1) {
        await API.put(endpoint, payload);
      } else {
        await API.update(endpoint, payload);
      }

      setState({ isModalLoading: false });
      handleEditTagModalClose();
      enqueueSnackbar('Tag updated successfully', { variant: 'success' });

      if (state.activeTabIndex === 0 && props.onTagsChanged) {
        props.onTagsChanged();
      } else if (state.activeTabIndex === 1) {
        refreshScenarioLinkTagsCombo();
      }

      setTimeout(() => {
        loadTags();
      }, 100);
    } catch (error: any) {
      setState({ isModalLoading: false });
      enqueueSnackbar(getTagErrorMessage(error, 'Failed to update tag'), {
        variant: 'error',
      });
    }
  };

  const handleDeleteTag = async (): Promise<void> => {
    if (!state.selectedTag) return;

    setState({ isModalLoading: true });

    try {
      const endpoint =
        state.activeTabIndex === 0
          ? `/clients/${props.clientId}/scenario_tags/${state.selectedTag.id}`
          : `/clients/${props.clientId}/scenario_link_tags/${state.selectedTag.id}`;

      await API.remove(endpoint);

      setState({ isModalLoading: false });
      handleDeleteTagModalClose();
      enqueueSnackbar('Tag deleted successfully', { variant: 'success' });

      if (state.activeTabIndex === 0 && props.onTagsChanged) {
        props.onTagsChanged();
      } else if (state.activeTabIndex === 1) {
        refreshScenarioLinkTagsCombo();
      }

      setTimeout(() => {
        loadTags();
      }, 100);
    } catch (error: any) {
      setState({ isModalLoading: false });

      if (error?.body?.error === 'TAG_IN_USE') {
        const { tagId, tagName, totalBlockers } = error.body;
        handleDeleteTagModalClose();
        tagInUseSnackbarKeyRef.current = enqueueSnackbar(
          `Cannot delete this tag - it's used in ${totalBlockers} assignment${
            totalBlockers === 1 ? '' : 's'
          }.`,
          {
            variant: 'error',
            persist: true,
            action: (snackbarId) => (
              <>
                <Button
                  size="small"
                  sx={{ color: 'common.white' }}
                  onClick={() => {
                    closeSnackbar(snackbarId);
                    setState({ tagInUseBlocker: { tagId, tagName } });
                  }}
                >
                  View Details
                </Button>
                <IconButton
                  size="small"
                  sx={{ color: 'common.white' }}
                  onClick={() => closeSnackbar(snackbarId)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </>
            ),
          }
        );
        return;
      }

      enqueueSnackbar(getTagErrorMessage(error, 'Failed to delete tag'), {
        variant: 'error',
      });
    }
  };

  const dateFormatter = (
    params: ICellRendererParams<ScenarioTag>
  ): JSX.Element => {
    if (params.value) {
      return (
        <OverflowTooltip
          {...params}
          value={format(new Date(params.value), 'MM/dd/yyyy hh:mm:ss a')}
        />
      );
    }
    return <Typography>-</Typography>;
  };

  const lastEditedByFormatter = (
    params: ICellRendererParams<ScenarioTag>
  ): JSX.Element => {
    // Handle both AI Tags (updated_by) and Submission Tags (created_by)
    const displayName =
      params.data?.updated_by?.Display_Name ||
      `${params.data?.created_by?.FirstName || ''} ${params.data?.created_by?.LastName || ''}`.trim();
    const userName =
      params.data?.updated_by?.User_Name ||
      params.data?.created_by?.FirstName ||
      '';

    if (displayName) {
      return (
        <Tooltip
          title={userName}
          arrow
          placement="bottom-start"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <Typography
            component="span"
            noWrap
            variant="body2"
            sx={{
              lineHeight: 'inherit',
              color: 'primary.main',
              textDecoration: 'underline',
              display: 'inline-block',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {displayName}
          </Typography>
        </Tooltip>
      );
    }
    return <Typography>-</Typography>;
  };

  const actionFormatter = (
    params: ICellRendererParams<ScenarioTag>
  ): JSX.Element => {
    return (
      <PopupState variant="popover" popupId="manage-scenario-tags-menu">
        {(popupState) => (
          <>
            <IconButton {...bindTrigger(popupState)}>
              <MoreHoriz />
            </IconButton>
            <StyledMenu {...bindMenu(popupState)}>
              <MenuItem
                onClick={() => {
                  popupState.close();
                  handleEditTagModalOpen(params.data as ScenarioTag);
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  popupState.close();
                  handleDeleteTagModalOpen(params.data as ScenarioTag);
                }}
              >
                Delete
              </MenuItem>
            </StyledMenu>
          </>
        )}
      </PopupState>
    );
  };

  const scenarioTagsColumns: ColDef[] = [
    {
      field: 'name',
      headerName: 'Tag Name',
      cellRenderer: OverflowTooltip,
      sortable: true,
      sort: 'asc',
      flex: 2,
    },
    {
      field: 'description',
      headerName: 'Description',
      cellRenderer: OverflowTooltip,
      sortable: true,
      flex: 3,
    },
    {
      field: 'updated_at',
      headerName: 'Last Edited At',
      cellRenderer: dateFormatter,
      sortable: true,
      flex: 2,
    },
    {
      field: 'updated_by.Display_Name',
      headerName: 'Last Edited By',
      cellRenderer: lastEditedByFormatter,
      sortable: true,
      flex: 2,
    },
    {
      field: 'id',
      headerName: 'Actions',
      cellRenderer: actionFormatter,
      flex: 1,
    },
  ];

  return (
    <>
      {/* Add Tag Modal */}
      <PopupDialog
        open={state.isAddTagModalOpen}
        title={state.activeTabIndex === 0 ? 'Add AI Tag' : 'Add Submission Tag'}
        close={handleAddTagModalClose}
        primaryButtons={[
          {
            id: 'add-tag-button',
            label: 'Add',
            onClick: handleAddTag,
            disabled:
              !state.tagName.trim() ||
              !state.tagDescription.trim() ||
              state.isModalLoading,
          },
        ]}
      >
        <Box>
          <TextField
            fullWidth
            label="Tag Name"
            required
            size="small"
            helperText={
              state.onFocusedInput === 'tagName' &&
              `${TAG_NAME_LENGTH - state.tagName.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'tagName' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagName: trimStart(
                  e.target.value.substring(0, TAG_NAME_LENGTH)
                ),
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            required
            multiline
            rows={3}
            size="small"
            helperText={
              state.onFocusedInput === 'description' &&
              `${DESCRIPTION_LENGTH - state.tagDescription.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'description' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagDescription}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagDescription: e.target.value.substring(0, DESCRIPTION_LENGTH),
              })
            }
          />
        </Box>
      </PopupDialog>

      {/* Edit Tag Modal */}
      <PopupDialog
        open={state.isEditTagModalOpen}
        title={
          state.activeTabIndex === 0 ? 'Edit AI Tag' : 'Edit Submission Tag'
        }
        close={handleEditTagModalClose}
        primaryButtons={[
          {
            id: 'edit-tag-button',
            label: 'Save',
            onClick: handleEditTag,
            disabled:
              !state.tagName.trim() ||
              !state.tagDescription.trim() ||
              state.isModalLoading,
          },
        ]}
      >
        <Box>
          <TextField
            fullWidth
            label="Tag Name"
            required
            size="small"
            helperText={
              state.onFocusedInput === 'tagName' &&
              `${TAG_NAME_LENGTH - state.tagName.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'tagName' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagName: trimStart(
                  e.target.value.substring(0, TAG_NAME_LENGTH)
                ),
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            required
            multiline
            rows={3}
            size="small"
            helperText={
              state.onFocusedInput === 'description' &&
              `${DESCRIPTION_LENGTH - state.tagDescription.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'description' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagDescription}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagDescription: e.target.value.substring(0, DESCRIPTION_LENGTH),
              })
            }
          />
        </Box>
      </PopupDialog>

      {/* Delete Tag Modal */}
      <PopupDialog
        open={state.showDeleteTagModal}
        title={
          state.activeTabIndex === 0
            ? 'Delete AI Tag?'
            : 'Delete Submission Tag?'
        }
        close={handleDeleteTagModalClose}
        isLoading={state.isModalLoading}
        primaryButtons={[
          {
            id: 'delete-tag-button',
            label: 'Yes, delete it',
            onClick: handleDeleteTag,
            disabled:
              state.isModalLoading ||
              state.deleteConfirmationName !== state.selectedTag?.name,
          },
        ]}
      >
        <Typography>
          This action <strong>cannot</strong> be undone. Are you absolutely sure
          you want to delete "<strong>{state.selectedTag?.name}</strong>"?
        </Typography>

        <TextField
          fullWidth
          value={state.deleteConfirmationName}
          label="Please type the name of this tag to confirm."
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setState({ deleteConfirmationName: e.target.value })
          }
          sx={{ mt: 2 }}
        />
        {state.activeTabIndex === 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'warning.lighter',
              borderLeft: 3,
              borderColor: 'warning.main',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Once deleted, all scenarios currently
              associated with this tag will be automatically unlinked.
            </Typography>
          </Box>
        )}
      </PopupDialog>

      {/* Main Dialog */}
      <PopupDialog
        title="Manage Scenario Tags"
        open={props.isManageScenarioTagsDialogOpen}
        close={() => {
          // Close any persistent "tag in use" snackbar when the dialog closes
          if (tagInUseSnackbarKeyRef.current !== null) {
            closeSnackbar(tagInUseSnackbarKeyRef.current);
            tagInUseSnackbarKeyRef.current = null;
          }
          props.handleCloseManageScenarioTagsDialog();
          setState(initialState);
        }}
        maxWidth={width > 1600 ? 'lg' : 'xl'}
        hideDialogActions
      >
        <Box>
          <Tabs
            value={state.activeTabIndex}
            onChange={(e, newValue) =>
              setState({
                activeTabIndex: newValue,
                ...(newValue === 0
                  ? { isAiTagsLoading: true }
                  : { isPromotionTagsLoading: true }),
              })
            }
            aria-label="tag types"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="AI Tags" />
            <Tab label="Submission Tags" />
          </Tabs>

          {/* AI Tags Tab Content */}
          {state.activeTabIndex === 0 && (
            <Box role="tabpanel">
              <TagsTableContent
                key="ai-tags"
                tags={state.aiTags ?? []}
                isDataLoading={state.isAiTagsLoading}
                onRefresh={loadTags}
                onAddTag={handleAddTagModalOpen}
                columnDefs={scenarioTagsColumns}
                tableId="ai-tags-table"
              />
            </Box>
          )}

          {/* Submission Tags Tab Content */}
          {state.activeTabIndex === 1 && (
            <Box role="tabpanel">
              <TagsTableContent
                key="promotion-tags"
                tags={state.promotionTags ?? []}
                isDataLoading={state.isPromotionTagsLoading}
                onRefresh={loadTags}
                onAddTag={handleAddTagModalOpen}
                columnDefs={scenarioTagsColumns}
                tableId="promotion-tags-table"
                infoText="Submission Tags identify a single scenario within each model instance as the promoted version for that tag. Target models use these tags in PTDL subscriptions to consume data from promoted source scenarios, and reports use them to report on scenarios assigned a specific promotion tag."
              />
            </Box>
          )}
        </Box>
      </PopupDialog>

      <TagBlockersDialog
        open={!!state.tagInUseBlocker}
        clientId={props.clientId}
        tagId={state.tagInUseBlocker?.tagId ?? null}
        tagName={state.tagInUseBlocker?.tagName ?? ''}
        onClose={() => setState({ tagInUseBlocker: null })}
      />
    </>
  );
};

export default ManageScenarioTags;
